import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Sun, Moon, Lightbulb, Layers, Sparkles, CircleDot, Play, Pause } from "lucide-react";

const KELVIN_STOPS = [
  [2200, 255, 137, 41],
  [2700, 255, 166, 92],
  [3000, 255, 184, 120],
  [3300, 255, 201, 150],
  [4000, 255, 223, 191],
  [4500, 255, 238, 218],
  [5000, 255, 250, 244],
  [5500, 246, 247, 255],
  [6000, 223, 232, 255],
  [6500, 203, 222, 250],
];

function kelvinToRgb(k) {
  const s = KELVIN_STOPS;
  if (k <= s[0][0]) return `rgb(${s[0][1]}, ${s[0][2]}, ${s[0][3]})`;
  const last = s[s.length - 1];
  if (k >= last[0]) return `rgb(${last[1]}, ${last[2]}, ${last[3]})`;
  for (let i = 0; i < s.length - 1; i++) {
    const a = s[i], b = s[i + 1];
    if (k >= a[0] && k <= b[0]) {
      const t = (k - a[0]) / (b[0] - a[0]);
      const r = Math.round(a[1] + (b[1] - a[1]) * t);
      const g = Math.round(a[2] + (b[2] - a[2]) * t);
      const bl = Math.round(a[3] + (b[3] - a[3]) * t);
      return `rgb(${r}, ${g}, ${bl})`;
    }
  }
  return `rgb(${last[1]}, ${last[2]}, ${last[3]})`;
}

function Frame({ children }) {
  return (
    <div className="rounded-xl glass p-5 sm:p-6 my-2">{children}</div>
  );
}

function ColorTemp() {
  const [k, setK] = useState(3000);
  const color = kelvinToRgb(k);
  const label = k < 3300 ? "Warmweiss" : k <= 5000 ? "Neutralweiss" : "Tageslichtweiss";
  return (
    <Frame>
      <div
        className="relative h-44 rounded-lg overflow-hidden border border-white/10 transition-colors duration-500"
        style={{ background: `radial-gradient(120% 90% at 50% 0%, ${color}, #0a0d12 75%)` }}
      >
        <div
          className="absolute top-5 left-1/2 -translate-x-1/2 h-10 w-24 rounded-full blur-md transition-all duration-500"
          style={{ background: color, opacity: 0.9 }}
        />
      </div>
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-glow"><Sun className="h-4 w-4" /></div>
        <div className="text-center">
          <div className="font-mono text-2xl font-medium text-foreground">{k} K</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        </div>
        <div className="flex items-center gap-2 text-cool" style={{ color: "var(--cool)" }}><Moon className="h-4 w-4" /></div>
      </div>
      <Slider value={[k]} min={2200} max={6500} step={100} onValueChange={([v]) => setK(v)} className="mt-4" data-testid="widget-colortemp-slider" />
    </Frame>
  );
}

function InverseSquare() {
  const [d, setD] = useState(2);
  const e = Math.round(100 / (d * d));
  // geometry: source fixed at top, illuminated surface moves down with distance
  const apexY = 22;
  const maxY = 156;
  const surfaceY = apexY + (d / 6) * (maxY - apexY);
  const halfW = (surfaceY - apexY) * 0.42;
  const patchOp = Math.max(0.08, Math.min(0.95, (e / 100) * 0.9 + 0.06));
  return (
    <Frame>
      <div className="relative h-44 rounded-lg overflow-hidden border border-white/10 bg-gradient-to-b from-[#10141b] to-[#070a0e]">
        <Lightbulb className="absolute top-2 left-1/2 -translate-x-1/2 h-6 w-6 text-gold z-10" />
        <svg viewBox="0 0 300 176" className="absolute inset-0 w-full h-full">
          <defs>
            <linearGradient id="is-beam" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,166,35,0.34)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0.04)" />
            </linearGradient>
            <radialGradient id="is-patch" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,205,120,1)" />
              <stop offset="70%" stopColor="rgba(245,166,35,0.85)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0)" />
            </radialGradient>
          </defs>
          {/* source glow */}
          <circle cx="150" cy={apexY} r="7" fill="rgba(245,205,140,0.6)" />
          {/* distance guide */}
          <line x1="150" y1={apexY} x2="150" y2={surfaceY} stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="3 3" style={{ transition: "all 0.3s ease" }} />
          {/* light beam (constant solid angle, widening with distance) */}
          <polygon points={`150,${apexY} ${150 - halfW},${surfaceY} ${150 + halfW},${surfaceY}`} fill="url(#is-beam)" style={{ transition: "all 0.3s ease" }} />
          {/* illuminated surface line */}
          <line x1="26" y1={surfaceY} x2="274" y2={surfaceY} stroke="rgba(255,255,255,0.14)" strokeWidth="1" style={{ transition: "all 0.3s ease" }} />
          {/* illuminated patch: grows with distance, dims with 1/r^2 */}
          <ellipse cx="150" cy={surfaceY} rx={halfW} ry="7" fill="url(#is-patch)" opacity={patchOp} style={{ transition: "all 0.3s ease" }} />
        </svg>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-4 text-center">
        <div>
          <div className="font-mono text-2xl text-foreground">{d.toFixed(1)} m</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Abstand r</div>
        </div>
        <div>
          <div className="font-mono text-2xl text-gold">{e}%</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Beleuchtungsstärke</div>
        </div>
      </div>
      <Slider value={[d * 10]} min={10} max={60} step={5} onValueChange={([v]) => setD(v / 10)} className="mt-4" data-testid="widget-inversesquare-slider" />
      <p className="mt-3 text-xs text-muted-foreground text-center font-mono">E = I / r²  →  doppelter Abstand = ein Viertel Licht (Fläche vervierfacht)</p>
    </Frame>
  );
}

function BeamAngle() {
  const [a, setA] = useState(24);
  const half = (a / 2) * (Math.PI / 180);
  const depth = 150;
  const half_w = Math.tan(half) * depth;
  const intensity = Math.round(100 * (24 / a));
  const kind = a <= 20 ? "Akzent / Brillanz" : a <= 40 ? "Ausgewogen" : "Grundlicht / Flut";
  return (
    <Frame>
      <div className="relative h-44 rounded-lg overflow-hidden border border-white/10 bg-gradient-to-b from-[#10141b] to-[#070a0e] flex justify-center">
        <CircleDot className="absolute top-2 h-5 w-5 text-gold z-10" />
        <svg width="100%" height="100%" viewBox="0 0 300 176" className="absolute inset-0">
          <defs>
            <linearGradient id="beam" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,166,35,0.55)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0.05)" />
            </linearGradient>
          </defs>
          <polygon
            points={`150,18 ${150 - half_w},168 ${150 + half_w},168`}
            fill="url(#beam)"
            style={{ transition: "all 0.4s ease" }}
          />
          <ellipse cx="150" cy="168" rx={half_w} ry="6" fill="rgba(245,166,35,0.4)" style={{ transition: "all 0.4s ease" }} />
        </svg>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-4 text-center">
        <div>
          <div className="font-mono text-2xl text-foreground">{a}°</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Abstrahlwinkel</div>
        </div>
        <div>
          <div className="font-mono text-2xl text-gold">{intensity}%</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">rel. Intensität</div>
        </div>
      </div>
      <div className="text-center text-xs text-gold mt-1">{kind}</div>
      <Slider value={[a]} min={8} max={60} step={2} onValueChange={([v]) => setA(v)} className="mt-4" data-testid="widget-beamangle-slider" />
    </Frame>
  );
}

function DirectIndirect() {
  const [mode, setMode] = useState("direct");
  const cfg = {
    direct: {
      label: "Direkt",
      img: "https://static.prod-images.emergentagent.com/jobs/ce1cbc9d-1dad-4cd9-98ed-a62f3c8b6634/images/d7958f7f27a7b3c160d3934b66e8e9ebb37e7b0f9b38cdf449f212fb2c81cbbc.png",
      source: "Einzelner, gerichteter Spot",
      desc: "Klare Lichtseite und dunkle Schattenseite, harter scharf umrissener Schlagschatten. Hohe Plastizität – ideal für Akzente, kann aber blenden.",
    },
    indirect: {
      label: "Indirekt",
      img: "https://static.prod-images.emergentagent.com/jobs/ce1cbc9d-1dad-4cd9-98ed-a62f3c8b6634/images/c284cc907740c6807d8d3605d1813299327f0d11e6a8fc89895b7938e9899550.png",
      source: "Über Decke und Wände reflektiert",
      desc: "Gleichmässig von allen Seiten, fast schattenlos. Ruhig und blendfrei, aber sehr flache Modellierung – kostet Wirkungsgrad.",
    },
    diffuse: {
      label: "Diffus",
      img: "https://static.prod-images.emergentagent.com/jobs/ce1cbc9d-1dad-4cd9-98ed-a62f3c8b6634/images/d3190c06581b30769fdb0dea29d29dbbd4427000494e82269d1be2e93e1cf761.png",
      source: "Grosse, weiche Lichtfläche",
      desc: "Weicher Verlauf von Hell zu Dunkel, weicher auslaufender Schatten. Ausgewogene Plastizität zwischen hart und flach.",
    },
  };
  const c = cfg[mode];
  return (
    <Frame>
      <div className="flex gap-2 mb-4">
        {Object.keys(cfg).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            data-testid={`widget-directindirect-${m}`}
            className={`flex-1 py-2 rounded-lg text-sm transition-all duration-300 border ${
              mode === m ? "bg-gold/15 border-gold/40 text-gold" : "border-white/10 text-muted-foreground hover:bg-white/5"
            }`}
          >
            {cfg[m].label}
          </button>
        ))}
      </div>
      <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#0a0d12]">
        <motion.img
          key={mode}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45 }}
          src={c.img}
          alt={`Lichtart ${c.label} an einer Marmorbüste`}
          className="w-full h-72 object-cover object-top"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-black/55 backdrop-blur-sm px-3 py-1.5 border border-white/10">
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          <span className="text-xs text-white/90">{c.source}</span>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground text-center">{c.desc}</p>
    </Frame>
  );
}

function LightHierarchy() {
  const [layers, setLayers] = useState({ grund: true, akzent: false, brillanz: false });
  const toggle = (k) => setLayers((s) => ({ ...s, [k]: !s[k] }));

  const items = [
    { k: "grund", label: "Grundlicht", icon: Layers, role: "funktionale Grundhelligkeit" },
    { k: "akzent", label: "Akzentlicht", icon: CircleDot, role: "räumliche Hierarchie" },
    { k: "brillanz", label: "Brillanz", icon: Sparkles, role: "Glanz & Materialität" },
  ];
  const activeRoles = items.filter((it) => layers[it.k]).map((it) => it.label);
  const count = activeRoles.length;
  const statusText =
    count === 0
      ? "Raum wirkt flach – keine Lichtebene aktiv"
      : activeRoles.join(" + ");

  return (
    <Frame>
      <div className="relative h-48 rounded-lg overflow-hidden border border-white/10 bg-[#0c0f15]">
        <svg viewBox="0 0 400 230" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="lh-pool" cx="50%" cy="35%" r="65%">
              <stop offset="0%" stopColor="rgba(245,190,120,0.55)" />
              <stop offset="55%" stopColor="rgba(245,166,35,0.18)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0)" />
            </radialGradient>
            <linearGradient id="lh-cone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,200,140,0.30)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0)" />
            </linearGradient>
            <radialGradient id="lh-spark" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
          </defs>

          {/* --- Base scene (room) --- */}
          <rect x="0" y="0" width="400" height="166" fill="#171b22" />
          <rect x="0" y="166" width="400" height="64" fill="#0f131a" />
          <line x1="0" y1="166" x2="400" y2="166" stroke="#262b34" strokeWidth="1" />

          {/* Artwork on the wall (accent object A) */}
          <rect x="54" y="34" width="68" height="88" rx="2" fill="#262b35" stroke="#3a414f" strokeWidth="1.5" />
          <rect x="61" y="41" width="54" height="74" fill="#1d222c" />
          {/* Console under the artwork */}
          <rect x="40" y="150" width="146" height="40" rx="2" fill="#1b1f28" />
          <rect x="40" y="150" width="146" height="4" fill="#272d38" />

          {/* Pedestal + vase (accent object B) */}
          <rect x="272" y="150" width="74" height="40" rx="2" fill="#1b1f28" />
          <rect x="272" y="150" width="74" height="4" fill="#272d38" />
          <path d="M300,150 C295,128 304,118 309,112 C314,118 323,128 318,150 Z" fill="#2c313b" />
          <rect x="305" y="108" width="8" height="6" rx="1.5" fill="#333945" />

          {/* --- Grundlicht: even ambient brightness --- */}
          <rect
            x="0" y="0" width="400" height="230" fill="#000"
            style={{ opacity: layers.grund ? 0.1 : 0.62, transition: "opacity 0.55s ease" }}
          />
          <rect
            x="0" y="0" width="400" height="230" fill="rgba(180,195,220,0.10)"
            style={{ opacity: layers.grund ? 1 : 0, transition: "opacity 0.55s ease" }}
          />

          {/* --- Akzentlicht: precise pools on the two objects --- */}
          <motion.g
            initial={false}
            animate={{ opacity: layers.akzent ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ pointerEvents: "none" }}
          >
            <polygon points="78,0 98,0 116,120 60,120" fill="url(#lh-cone)" />
            <ellipse cx="88" cy="78" rx="42" ry="54" fill="url(#lh-pool)" />
            <polygon points="300,0 318,0 332,150 286,150" fill="url(#lh-cone)" />
            <ellipse cx="309" cy="132" rx="34" ry="44" fill="url(#lh-pool)" />
          </motion.g>

          {/* --- Brillanz: fine specular highlights on the objects --- */}
          <motion.g
            initial={false}
            animate={{ opacity: layers.brillanz ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            style={{ pointerEvents: "none" }}
          >
            <circle cx="121" cy="36" r="5" fill="url(#lh-spark)" />
            <circle cx="307" cy="113" r="4.5" fill="url(#lh-spark)" />
            <circle cx="312" cy="138" r="3.5" fill="url(#lh-spark)" />
            <circle cx="56" cy="120" r="3" fill="url(#lh-spark)" />
          </motion.g>
        </svg>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        {items.map((it) => {
          const Icon = it.icon;
          const on = layers[it.k];
          return (
            <button
              key={it.k}
              onClick={() => toggle(it.k)}
              data-testid={`widget-hierarchy-${it.k}`}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs transition-all duration-300 ${
                on ? "bg-gold/15 border-gold/40 text-gold" : "border-white/10 text-muted-foreground hover:bg-white/5"
              }`}
            >
              <Icon className="h-4 w-4" />
              {it.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg glass px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Aktive Lichtebenen</span>
          <span className="font-mono text-sm text-gold">{count}/3</span>
        </div>
        <div className="text-right text-xs sm:text-sm text-foreground/80">{statusText}</div>
      </div>
    </Frame>
  );
}

function LuxSteps() {
  const [active, setActive] = useState(0);
  const steps = [
    { t: "Raum", d: "Geometrie, Höhe, Materialien, Reflexionsgrade und Tageslicht analysieren. Was ist das für ein Raum?" },
    { t: "Nutzung", d: "Tätigkeiten, Sehaufgaben, Nutzergruppen und Tageszeiten erfassen. Was passiert hier?" },
    { t: "Wirkung", d: "Atmosphäre, Hierarchie und Blickführung definieren. Wie soll der Raum sich anfühlen?" },
    { t: "Technik", d: "Leuchtentypen, Lichtfarbe, Steuerung und Normen wählen. Womit erreiche ich das Ziel?" },
    { t: "Briefing", d: "Konzept kommunizieren – an Bauherrschaft und Fachplanung. Wie sichere ich die Umsetzung?" },
  ];
  return (
    <Frame>
      <div className="flex items-center gap-1.5 sm:gap-2 mb-5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center flex-1 last:flex-none">
            <button
              onClick={() => setActive(i)}
              data-testid={`widget-luxstep-${i}`}
              className={`h-9 w-9 sm:h-10 sm:w-10 rounded-full flex items-center justify-center text-sm font-mono border transition-all duration-300 flex-shrink-0 ${
                i === active ? "bg-gold text-[#0a0d12] border-gold scale-110" : "border-white/15 text-muted-foreground hover:border-gold/40"
              }`}
            >
              {i + 1}
            </button>
            {i < steps.length - 1 && <div className="h-px flex-1 bg-white/10 mx-1" />}
          </div>
        ))}
      </div>
      <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-xs uppercase tracking-widest text-gold">Schritt {active + 1}</div>
        <div className="font-heading font-bold text-xl mt-0.5">{steps[active].t}</div>
        <p className="text-sm text-foreground/75 mt-2 leading-relaxed">{steps[active].d}</p>
      </motion.div>
    </Frame>
  );
}

function ContrastLux() {
  const [c, setC] = useState(15);
  const ambient = 0.5 - 0.4 * (c / 100);
  const accent = c / 100;
  const lux = Math.round(750 - (c / 100) * 430);
  const verdict =
    c < 25
      ? "Flach & monoton – viel Licht, wenig Wirkung"
      : c < 60
      ? "Erste Hierarchie und Tiefe entstehen"
      : "Tief & lebendig – weniger Lux, mehr Wirkung";
  return (
    <Frame>
      <div className="relative h-48 rounded-lg overflow-hidden border border-white/10 bg-[#0b0e14]">
        <svg viewBox="0 0 360 200" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="cl-pool" cx="50%" cy="30%" r="65%">
              <stop offset="0%" stopColor="rgba(250,200,130,0.85)" />
              <stop offset="55%" stopColor="rgba(245,166,35,0.30)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0)" />
            </radialGradient>
            <linearGradient id="cl-cone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(248,205,140,0.45)" />
              <stop offset="100%" stopColor="rgba(245,166,35,0)" />
            </linearGradient>
            <radialGradient id="cl-vig" cx="50%" cy="48%" r="75%">
              <stop offset="55%" stopColor="rgba(0,0,0,0)" />
              <stop offset="100%" stopColor="rgba(0,0,0,0.75)" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="360" height="142" fill="#161b23" />
          <rect x="0" y="142" width="360" height="58" fill="#0e1219" />
          <line x1="0" y1="142" x2="360" y2="142" stroke="#242a34" strokeWidth="1" />
          <rect x="214" y="30" width="66" height="80" rx="2" fill="#262c37" stroke="#39414f" strokeWidth="1.5" />
          <rect x="221" y="37" width="52" height="66" fill="#1d222c" />
          <rect x="44" y="150" width="150" height="34" rx="2" fill="#1b202a" />
          <rect x="44" y="150" width="150" height="4" fill="#283040" />
          <path d="M104,150 C100,130 108,121 113,116 C118,121 126,130 122,150 Z" fill="#2b313c" />
          {/* even flat wash – strong at low contrast */}
          <rect x="0" y="0" width="360" height="200" fill="rgba(206,214,230,1)" opacity={ambient} style={{ transition: "opacity 0.3s ease" }} />
          {/* accent layer – grows with contrast */}
          <g opacity={accent} style={{ transition: "opacity 0.3s ease" }}>
            <polygon points="236,0 258,0 272,110 222,110" fill="url(#cl-cone)" />
            <ellipse cx="247" cy="70" rx="42" ry="52" fill="url(#cl-pool)" />
            <ellipse cx="113" cy="150" rx="46" ry="16" fill="url(#cl-pool)" />
            <rect x="0" y="0" width="360" height="200" fill="url(#cl-vig)" opacity="0.6" />
          </g>
        </svg>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        <div className="rounded-lg glass px-4 py-3 text-center">
          <div className="font-mono text-2xl text-foreground">{lux} lx</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Mittlere Beleuchtungsstärke</div>
        </div>
        <div className="rounded-lg glass px-4 py-3 text-center">
          <div className="font-mono text-2xl text-gold">{c}%</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Kontrast / Tiefe</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>gleichmässig</span>
        <span>kontrastreich</span>
      </div>
      <Slider value={[c]} min={0} max={100} step={5} onValueChange={([v]) => setC(v)} className="mt-1" data-testid="widget-contrastlux-slider" />

      <p className="mt-4 text-sm text-center text-foreground/80">{verdict}</p>
      <p className="mt-1 text-xs text-center text-muted-foreground">Das Auge bewertet Kontraste, nicht absolute Lux.</p>
    </Frame>
  );
}


const HCL_STOPS = [
  [6, 2900, 22],
  [8, 4000, 52],
  [10, 5200, 78],
  [12, 6000, 100],
  [14, 5500, 90],
  [16, 4300, 66],
  [18, 3300, 42],
  [20, 2800, 26],
  [22, 2700, 14],
];

function hclProfile(hour) {
  const s = HCL_STOPS;
  if (hour <= s[0][0]) return { kelvin: s[0][1], bright: s[0][2] };
  const last = s[s.length - 1];
  if (hour >= last[0]) return { kelvin: last[1], bright: last[2] };
  for (let i = 0; i < s.length - 1; i++) {
    const a = s[i], b = s[i + 1];
    if (hour >= a[0] && hour <= b[0]) {
      const t = (hour - a[0]) / (b[0] - a[0]);
      return { kelvin: Math.round(a[1] + (b[1] - a[1]) * t), bright: Math.round(a[2] + (b[2] - a[2]) * t) };
    }
  }
  return { kelvin: last[1], bright: last[2] };
}

function HumanCentric() {
  const [hour, setHour] = useState(12);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setHour((h) => {
        const next = Math.round((h + 0.5) * 10) / 10;
        return next > 22 ? 6 : next;
      });
    }, 240);
    return () => clearInterval(id);
  }, [playing]);

  const { kelvin, bright } = hclProfile(hour);
  const color = kelvinToRgb(kelvin);
  const lux = Math.round((bright / 100) * 750);
  const hh = Math.floor(hour);
  const mm = Math.round((hour - hh) * 60);
  const timeStr = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  const isDay = hour >= 7 && hour <= 19;
  const win = 0.25 + (bright / 100) * 0.75;
  const spill = Math.max(0, 1 - bright / 100) * 0.55;

  const p = (hour - 6) / 16;
  const sunX = 8 + p * 84;
  const sunY = 72 - Math.sin(Math.max(0, Math.min(1, p)) * Math.PI) * 54;

  let phase, note;
  if (hour < 9) { phase = "Morgen – aktivierend"; note = "Kühleres, ansteigendes Licht unterstützt das Wachwerden."; }
  else if (hour < 15) { phase = "Mittag – maximale Aktivierung"; note = "Hohes, kühles Licht fördert Konzentration und Leistung."; }
  else if (hour < 18) { phase = "Nachmittag – ausklingend"; note = "Das Licht wird wärmer und ruhiger, die Aktivierung sinkt."; }
  else { phase = "Abend – beruhigend"; note = "Warmes, gedämpftes Licht unterstützt die Melatonin-Ausschüttung."; }

  const SunIcon = isDay ? Sun : Moon;

  return (
    <Frame>
      <div className="relative h-48 rounded-lg overflow-hidden border border-white/10" style={{ background: "#070a0e" }}>
        {/* sky following the day */}
        <div
          className="absolute inset-0 transition-all duration-300"
          style={{ background: `radial-gradient(130% 95% at ${sunX}% 0%, ${color}, #070a0e 80%)`, opacity: 0.2 + (bright / 100) * 0.8 }}
        />
        {/* sun / moon */}
        <div className="absolute transition-all duration-300" style={{ left: `${sunX}%`, top: `${sunY}%`, transform: "translate(-50%, -50%)" }}>
          <SunIcon className="h-6 w-6" style={{ color: isDay ? color : "#9fb4d8" }} />
        </div>
        {/* house with windows glowing in the current light colour */}
        <svg viewBox="0 0 220 150" preserveAspectRatio="xMidYMax meet" className="absolute inset-0 w-full h-full">
          <defs>
            <filter id="hclGlow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="3.5" />
            </filter>
            <filter id="hclSpillBlur" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
            <linearGradient id="hclSpill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.85" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
            <radialGradient id="hclPool" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={color} stopOpacity="0.7" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* ground */}
          <rect x="0" y="120" width="220" height="30" fill="#0a0e14" />
          <line x1="0" y1="120" x2="220" y2="120" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          {/* roof */}
          <polygon points="66,80 110,46 154,80" fill="#0c1118" stroke="rgba(255,255,255,0.16)" strokeWidth="1.2" />
          {/* body */}
          <rect x="78" y="80" width="64" height="40" fill="#11161e" stroke="rgba(255,255,255,0.16)" strokeWidth="1.2" />
          {/* window glow */}
          <rect x="83" y="86" width="18" height="16" rx="2" fill={color} opacity={win * 0.5} filter="url(#hclGlow)" />
          <rect x="119" y="86" width="18" height="16" rx="2" fill={color} opacity={win * 0.5} filter="url(#hclGlow)" />
          {/* windows */}
          <rect x="85" y="88" width="14" height="12" rx="1" fill={color} opacity={win} stroke="rgba(0,0,0,0.35)" strokeWidth="0.8" />
          <rect x="121" y="88" width="14" height="12" rx="1" fill={color} opacity={win} stroke="rgba(0,0,0,0.35)" strokeWidth="0.8" />
          {/* door */}
          <rect x="104" y="104" width="12" height="16" rx="1" fill={color} opacity={win * 0.7} stroke="rgba(255,255,255,0.12)" strokeWidth="0.8" />
          {/* light spill from the windows onto the ground (strongest in the evening) */}
          <g opacity={spill} filter="url(#hclSpillBlur)" style={{ transition: "opacity 0.3s" }}>
            <polygon points="88,101 96,101 120,140 64,140" fill="url(#hclSpill)" />
            <polygon points="124,101 132,101 156,140 100,140" fill="url(#hclSpill)" />
            <ellipse cx="110" cy="134" rx="64" ry="8" fill="url(#hclPool)" />
          </g>
        </svg>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg glass px-2 py-2">
          <div className="font-mono text-lg sm:text-xl text-foreground">{timeStr}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tageszeit</div>
        </div>
        <div className="rounded-lg glass px-2 py-2">
          <div className="font-mono text-lg sm:text-xl text-gold">{kelvin} K</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Lichtfarbe</div>
        </div>
        <div className="rounded-lg glass px-2 py-2">
          <div className="font-mono text-lg sm:text-xl text-foreground">{lux} lx</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Helligkeit</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={() => setPlaying((v) => !v)}
          data-testid="widget-hcl-play"
          className={`h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full border transition-all duration-300 ${playing ? "bg-gold/15 border-gold/40 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40 hover:text-gold"}`}
          title={playing ? "Pause" : "Tagesverlauf abspielen"}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <Slider value={[hour * 10]} min={60} max={220} step={5} onValueChange={([v]) => { setPlaying(false); setHour(v / 10); }} className="flex-1" data-testid="widget-hcl-slider" />
      </div>

      <div className="mt-4 rounded-lg glass px-4 py-3">
        <div className="text-sm font-heading font-bold text-gold">{phase}</div>
        <p className="text-xs text-foreground/75 mt-1 leading-relaxed">{note}</p>
      </div>
    </Frame>
  );
}


const WIDGETS = {
  color_temp: ColorTemp,
  human_centric: HumanCentric,
  inverse_square: InverseSquare,
  beam_angle: BeamAngle,
  direct_indirect: DirectIndirect,
  light_hierarchy: LightHierarchy,
  lux_steps: LuxSteps,
  contrast_lux: ContrastLux,
};

export default function InteractiveWidget({ widget }) {
  const Comp = WIDGETS[widget];
  if (!Comp) return null;
  return <Comp />;
}
